import { X } from 'lucide-react';
import Button from '../base-ui/button';
import { useLayoutVisibility } from '@/utils/common-utils';
import { ScrollArrowDownSVG } from '../svg-icons/common-icons';
import { useEffect, useRef, useState } from 'react';
import Image from '../base-ui/image';
import brandLogo from '@/assets/logo-image/logo-brandcolor.png';

export const ComponentModal: React.FC<{
  isOpen: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  showClose?: boolean;
  // modalSize?: 'lg' | 'xl' | '2xl' | '3xl';
}> = ({
  isOpen,
  onClose,
  children,
  showClose = true,
  // modalSize = 'lg'
}) => {
  const {
    isMobile,
    // isLandscape
    _isIOS,
  } = useLayoutVisibility();
  const contentRef = useRef<HTMLDivElement>(null);
  const [showArrow, setShowArrow] = useState(false);

  // Check if content is scrollable and not at bottom
  const checkScroll = () => {
    const el = contentRef.current;
    if (!el) return;
    const isScrollable = el.scrollHeight > el.clientHeight;
    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    setShowArrow(isScrollable && !isAtBottom);
  };

  useEffect(() => {
    checkScroll();
    const el = contentRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [isOpen, isMobile]);

  const handleArrowClick = () => {
    const el = contentRef.current;
    if (!el) return;
    el.scrollTo({
      top: el.scrollHeight * 0.85 - el.clientHeight,
      behavior: 'smooth',
    });
  };

  if (!isOpen) return null;
  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className={`relative z-10 mx-4 w-full max-w-lg bg-white rounded-lg shadow-xl ${isMobile ? `p-2` : ''}`}>
        <div ref={contentRef} className={`${isMobile ? 'h-full overflow-scroll p-2' : 'p-6'} relative`}>
          {showClose && (
            <div className="absolute top-2.5 right-2.5 cursor-pointer" onClick={onClose}>
              <X />
            </div>
          )}
          <div>{children}</div>
        </div>
        {showArrow && _isIOS && (
          <div
            className="absolute right-8 bottom-6 z-20 cursor-pointer animate-bounce"
            style={{ pointerEvents: 'auto' }}
            onClick={handleArrowClick}
          >
            <ScrollArrowDownSVG />
          </div>
        )}
      </div>
    </div>
  );
};

export const ComponentModalTransparent: React.FC<{
  isOpen: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  showClose?: boolean;
  // modalSize?: 'lg' | 'xl' | '2xl' | '3xl';
}> = ({
  isOpen,
  onClose,
  children,
  showClose = true,
  // modalSize = 'lg'
}) => {
  const {
    isMobile,
    // isLandscape
    _isIOS,
  } = useLayoutVisibility();
  const contentRef = useRef<HTMLDivElement>(null);
  const [showArrow, setShowArrow] = useState(false);

  // Check if content is scrollable and not at bottom
  const checkScroll = () => {
    const el = contentRef.current;
    if (!el) return;
    const isScrollable = el.scrollHeight > el.clientHeight;
    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    setShowArrow(isScrollable && !isAtBottom);
  };

  useEffect(() => {
    checkScroll();
    const el = contentRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [isOpen, isMobile]);

  const handleArrowClick = () => {
    const el = contentRef.current;
    if (!el) return;
    el.scrollTo({
      top: el.scrollHeight * 0.85 - el.clientHeight,
      behavior: 'smooth',
    });
  };

  if (!isOpen) return null;
  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className={`relative z-10 mx-4 w-full max-w-lg bg-transparent ${isMobile ? `p-2` : ''}`}>
        <div ref={contentRef} className={`${isMobile ? 'h-full overflow-auto p-2' : 'p-6'} relative`}>
          {showClose && (
            <div className="absolute top-2.5 right-2.5 cursor-pointer" onClick={onClose}>
              <X color="white" />
            </div>
          )}
          <div>{children}</div>
        </div>
        {showArrow && _isIOS && (
          <div
            className="absolute right-8 bottom-6 z-20 cursor-pointer animate-bounce"
            style={{ pointerEvents: 'auto' }}
            onClick={handleArrowClick}
          >
            <ScrollArrowDownSVG />
          </div>
        )}
      </div>
    </div>
  );
};

export const ComponentModalFullScreen: React.FC<{
  isOpen: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  showClose?: boolean;
  // modalSize?: 'lg' | 'xl' | '2xl' | '3xl';
}> = ({
  isOpen,
  onClose,
  children,
  showClose = true,
  // modalSize = 'lg'
}) => {
  const {
    isMobile,
    // isLandscape
    _isIOS,
  } = useLayoutVisibility();
  const contentRef = useRef<HTMLDivElement>(null);
  const [showArrow, setShowArrow] = useState(false);

  // Check if content is scrollable and not at bottom
  const checkScroll = () => {
    const el = contentRef.current;
    if (!el) return;
    const isScrollable = el.scrollHeight > el.clientHeight;
    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    setShowArrow(isScrollable && !isAtBottom);
  };

  useEffect(() => {
    checkScroll();
    const el = contentRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [isOpen, isMobile]);

  const handleArrowClick = () => {
    const el = contentRef.current;
    if (!el) return;
    el.scrollTo({
      top: el.scrollHeight * 0.85 - el.clientHeight,
      behavior: 'smooth',
    });
  };

  if (!isOpen) return null;
  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className={`relative z-10 w-full bg-white h-full`}>
        <div ref={contentRef} className={`${isMobile ? 'h-full overflow-auto p-2' : 'p-6'} relative`}>
          <div className="absolute top-2.5 left-2.5 cursor-pointer" onClick={onClose}>
            <div className="relative w-24">
              <Image src={brandLogo} alt="Logo" style={{ objectFit: 'contain' }} />
            </div>
          </div>
          {showClose && (
            <div className="absolute top-2.5 right-2.5 cursor-pointer" onClick={onClose}>
              <X size={38} color="var(--lifexlight)" />
            </div>
          )}
          <div>{children}</div>
        </div>
        {showArrow && _isIOS && (
          <div
            className="absolute right-8 bottom-6 z-20 cursor-pointer animate-bounce"
            style={{ pointerEvents: 'auto' }}
            onClick={handleArrowClick}
          >
            <ScrollArrowDownSVG />
          </div>
        )}
      </div>
    </div>
  );
};

export const EnhanceExperienceModal: React.FC<{
  header: string;
  mainContent?: React.ReactNode;
  subContent?: React.ReactNode;
  cost?: string;
  // upgrade?: () => void;
  pay?: () => void;
  ButtonContent?: React.ReactNode;
  BodyContent?: React.ReactNode;
}> = ({
  header,
  mainContent,
  subContent,
  cost,
  // upgrade = () => {},
  pay = () => {},
  ButtonContent,
  BodyContent,
}) => {
  return (
    <>
      <div className="text-center flex items-center justify-center flex-col">
        <span className="font-poppins block text-[var(--lifexlight)] text-[16px] font-semibold leading-[24px] mb-5">
          {header}
        </span>
        {mainContent && (
          <h3 className="text-[var(--lifexlight)] text-[26px] font-medium leading-[36px] mb-3">{mainContent}</h3>
        )}
        {subContent && <p className="mt-2 font-medium text-[13px] font-poppins text-gray-600">{subContent}</p>}
        {BodyContent && BodyContent}
      </div>
      {!ButtonContent && (
        <div className="flex justify-center mt-4 gap-6 p-2 pr-4 pl-4">
          <Button variant="outline" onClick={pay}>
            <div className="flex justify-center items-center">
              {/* {loading && <Spinner class="w-6 h-6 border-white border-4" />} */}
              <span>{cost}</span>
            </div>
          </Button>
          {/* <Button variant="primary" onClick={upgrade}>
              <div className="flex justify-center items-center">
                <span>Upgrade your plan</span>
              </div>
            </Button> */}
        </div>
      )}
      {ButtonContent && ButtonContent}
    </>
  );
};
